import React from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardActions, 
  Divider, 
  Box, 
  Typography, 
} from '@mui/material';

/**
 * A styled card component with header, content, and actions
 * 
 * @param {Object} props - Component props
 * @param {String} props.title - Card title
 * @param {String} props.subtitle - Optional card subtitle
 * @param {Node} props.headerAction - Optional action component for the header
 * @param {Node} props.headerIcon - Optional icon for the header
 * @param {Node} props.children - Card content
 * @param {Node} props.actions - Optional card actions
 * @param {Boolean} props.divider - Whether to show a divider after the header
 * @param {Object} props.sx - Additional styling for the card
 * @param {Boolean} props.noPadding - Whether to remove padding from content
 */
const CardWithHeader = ({
  title,
  subtitle,
  headerAction,
  headerIcon,
  children,
  actions,
  divider = true,
  sx = {},
  noPadding = false
}) => {

  return (
    <Card
      elevation={0}
      sx={{ 
        borderRadius: 3,
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx
      }}
    >
      {title && (
        <Box>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {headerIcon && (
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                    {headerIcon}
                  </Box>
                )}
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
              </Box>
            }
            subheader={subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            action={headerAction}
            sx={{ 
              pb: divider ? 1.5 : 0
            }}
          />
          {divider && <Divider />}
        </Box>
      )}
      
      <CardContent 
        sx={{ 
          flexGrow: 1,
          padding: noPadding ? 0 : undefined,
          '&:last-child': { pb: noPadding ? 0 : 3 }
        }}
      >
        {children}
      </CardContent>
      
      {actions && (
        <>
          <Divider />
          <CardActions>
            {actions}
          </CardActions>
        </>
      )}
    </Card>
  );
};

export default CardWithHeader;